import React, { useCallback, useMemo, useRef } from "react";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { subMinutes } from "date-fns/subMinutes";
import {
  Controller,
  ControllerRenderProps,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { DateTimePickerDropdown } from "src/base-components/DateTimePickerDropdown";
import { Select } from "src/base-components/Select";
import {
  PreviewQueryRequestEnvironmentEnum,
  PreviewQueryResponse,
} from "src/clients/features-control";
import { useFeatureQueryCopilot } from "src/copilot/useFeatureQueryCopilot";
import { Accordion } from "src/design-system/Accordion";
import { Callout } from "src/design-system/Callout";
import { CopilotButton } from "src/design-system/CopilotButton/CopilotButton";
import { Icon } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import {
  getPreviewColumnNames,
  MIN_COLUMNS_FOR_NON_GLOBAL_FEATURES,
} from "src/featureCatalogue/Queries/Create/helpers";
import { RunQueryButton } from "src/featureCatalogue/Queries/Create/RunQueryButton";
import {
  QueryFormValues,
  TIMESTAMP_BETWEEN_PATTERN,
  TIMESTAMP_VALIDATION_MESSAGE,
} from "src/featureCatalogue/Queries/Create/schema";
import {
  getCompletionExtension,
  getWindowHopOptions,
  getWindowLengthOptions,
  isWindowHopOptionDisabled,
  MIN_NON_TAKTILE_OPTION_VALUE,
} from "src/featureCatalogue/Queries/Create/utils";
import { UseFeatureQueryPreview } from "src/featureCatalogue/queries";
import { useIsTaktileOrgUser } from "src/hooks/useIsTaktileOrgUser";
import { logger } from "src/utils/logger";
import { errorMessage } from "src/utils/stringUtils";

type QuerySectionProps = {
  readOnly: boolean;
  featureQueryPreview: UseFeatureQueryPreview;
  handleFeatureGeneration?: (preview: PreviewQueryResponse) => void;
  isLoading: boolean;
  isEditMode: boolean;
  onExpandQuerySection: () => void;
};

export const SectionHeader: React.FC<{
  title: string;
  description?: string;
  tooltip?: React.ReactNode;
}> = ({ title, description, tooltip }) => {
  return (
    <div className="pb-2">
      <h3
        className={twJoin(
          "font-inter-semibold-13px text-gray-800",
          description && "pb-2",
        )}
      >
        <span>{title}</span>
        {tooltip && (
          <Tooltip body={tooltip}>
            <Icon icon={faInfoCircle} size="2xs" variant="hover" />
          </Tooltip>
        )}
      </h3>
      {description && (
        <p className="font-inter-normal-12px text-gray-500">{description}</p>
      )}
    </div>
  );
};

export const QuerySection: React.FC<QuerySectionProps> = ({
  readOnly,
  featureQueryPreview,
  handleFeatureGeneration,
  isLoading,
  isEditMode,
  onExpandQuerySection,
}) => {
  const form = useFormContext<QueryFormValues>();
  const isTaktileOrgUser = useIsTaktileOrgUser();

  const { openFeatureQueryCopilot: openCopilot } = useFeatureQueryCopilot();
  const queryErrorRef = useRef(featureQueryPreview.error);
  queryErrorRef.current = featureQueryPreview.error;

  const expandQuerySectionRef = useRef(onExpandQuerySection);
  expandQuerySectionRef.current = onExpandQuerySection;

  const [windowHop, windowLength] = useWatch({
    control: form.control,
    name: ["window_hop", "window_length"],
  });
  const handleRunQuery = useCallback(
    async (environment: PreviewQueryRequestEnvironmentEnum) => {
      try {
        const [queryString, currentWindowLength, endTime] = form.getValues([
          "query",
          "window_length",
          "end_datetime",
        ]);

        if (!queryString.length) return;

        if (!TIMESTAMP_BETWEEN_PATTERN.test(queryString)) {
          toastActions.failure({
            title: "Missing timestamp filter",
            description: TIMESTAMP_VALIDATION_MESSAGE,
          });
          return;
        }

        const triggerResponse = await featureQueryPreview.triggerPreview({
          queryString,
          windowLength: currentWindowLength,
          environment,
          endTime,
        });

        if (!isEditMode) {
          form.setValue(
            "columns.entities.0.entity_name",
            triggerResponse.detected_entity ?? "",
          );
        }

        // Wait for the preview data to be ready and get it from the promise
        const previewData = await featureQueryPreview.promise.current?.promise;

        if (previewData && handleFeatureGeneration) {
          // Generate new set of features — columns come from the returned rows
          // or, when the window has no data, from the backend column schema.
          handleFeatureGeneration(previewData);
        }
      } catch (error) {
        toastActions.failure({
          title: "Error running query",
          description:
            errorMessage(error) ||
            "An unexpected error occurred while running the query",
        });
        logger.error(error);
      }
    },
    [form, featureQueryPreview, handleFeatureGeneration, isEditMode],
  );

  const handleRunQueryShortcut = useCallback((): boolean => {
    const queryString = form.getValues("query");

    if (
      !queryString?.length ||
      !windowLength ||
      !windowHop ||
      featureQueryPreview.isPending
    ) {
      return false;
    }

    handleRunQuery(PreviewQueryRequestEnvironmentEnum.SANDBOX);
    return true;
  }, [form, windowLength, windowHop, featureQueryPreview, handleRunQuery]);

  const keyBindings = useMemo(
    () => [
      {
        mac: "Cmd-Enter",
        win: "Ctrl-Enter",
        linux: "Ctrl-Enter",
        run: handleRunQueryShortcut,
      },
    ],
    [handleRunQueryShortcut],
  );

  const handleOpenCopilot = useCallback(() => {
    openCopilot({
      getCurrentQuery: () => form.getValues("query"),
      getLastQueryErrorMessage: () => errorMessage(queryErrorRef.current) || "",
      onApplyQuery: (suggestion) => {
        form.setValue("query", suggestion.query);
        form.trigger("query");
        expandQuerySectionRef.current();
      },
    });
  }, [openCopilot, form]);

  const timeWindowOptions = getWindowLengthOptions().map((option) => {
    const value = parseInt(option.key);

    if (!isTaktileOrgUser && value < MIN_NON_TAKTILE_OPTION_VALUE) {
      return {
        ...option,
        disabled: "This time window is only available for Taktile users",
      };
    }

    return { ...option, disabled: false as const };
  });

  const refreshIntervalOptions = getWindowHopOptions().map((option) => {
    const value = parseInt(option.key);

    if (isWindowHopOptionDisabled(value, windowLength)) {
      return {
        ...option,
        disabled: "This option is not available for the selected time window",
      };
    }

    if (!isTaktileOrgUser && value < MIN_NON_TAKTILE_OPTION_VALUE) {
      return {
        ...option,
        disabled: "This refresh interval is only available for Taktile users",
      };
    }

    return { ...option, disabled: false as const };
  });

  const onChangeWindowLength = useCallback(
    (
      value: string,
      field: ControllerRenderProps<QueryFormValues, "window_length">,
    ) => {
      const parsedWindowLengthValue = parseInt(value);

      if (isWindowHopOptionDisabled(windowHop, parsedWindowLengthValue)) {
        form.resetField("window_hop", {
          // @ts-expect-error - we want to reset the field to null
          defaultValue: null,
        });
      }
      return field.onChange(parsedWindowLengthValue);
    },
    [form, windowHop],
  );

  return (
    <Accordion.Item
      dataLoc="sql-query-accordion"
      isLoading={isLoading}
      loadingSkeleton={<QuerySectionSkeleton />}
      rightSlot={
        <>
          {isLoading ? (
            <Skeleton height="h-7.5" rounded="rounded-lg" width="w-32" />
          ) : (
            <CopilotButton isDisabled={readOnly} onClick={handleOpenCopilot}>
              Ask Copilot
            </CopilotButton>
          )}
        </>
      }
      label="SQL Query"
      value="sql-query"
    >
      <div className="h-80 grow pb-6">
        <Controller
          name="query"
          render={({ field, fieldState }) => (
            <CodeEditor
              completionExtension={getCompletionExtension(readOnly)}
              dataLoc="sql-query-editor"
              disabled={readOnly}
              errorHighlightedLine={fieldState.error?.message ? 1 : undefined}
              keyBindings={keyBindings}
              language="sql"
              value={field.value}
              onBlur={() => form.formState.isSubmitted && form.trigger()}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      {/* Setup timing and Run Query Section */}
      <div>
        <div className="flex items-center gap-x-4 pb-4">
          <div className="flex-1">
            <SectionHeader
              title="Time window"
              tooltip="The time window for calculating your feature (e.g., last 30 days of transactions). Maximum 365 days."
            />
            <Controller
              control={form.control}
              name="window_length"
              render={({ field }) => (
                <Select
                  dataLoc="window-length-select"
                  disabled={readOnly}
                  errored={!!form.formState.errors.window_length}
                  options={timeWindowOptions}
                  placeholder="Select time window"
                  value={field.value ? field.value.toString() : null}
                  searchable
                  onChange={(value) => onChangeWindowLength(value, field)}
                />
              )}
            />
          </div>
          <div className="flex-1">
            <SectionHeader title="End date and time for preview" />
            <Controller
              control={form.control}
              defaultValue={subMinutes(new Date(), 1)}
              name="end_datetime"
              render={({ field }) => (
                <DateTimePickerDropdown
                  disabled={readOnly}
                  errored={!!form.formState.errors.end_datetime}
                  placeholder="Select end date and time"
                  value={field.value as Date | undefined}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </div>
        <div className="pb-6">
          <SectionHeader
            title="Refresh interval"
            tooltip="How often your feature values are updated. Shorter intervals mean more recent data. Match to your decision-making needs (e.g., 1 min for real-time fraud, 1 day for periodic reviews)"
          />
          <Controller
            control={form.control}
            name="window_hop"
            render={({ field }) => {
              return (
                <Select
                  dataLoc="window-hop-select"
                  disabled={readOnly}
                  errored={!!form.formState.errors.window_hop}
                  options={refreshIntervalOptions}
                  placeholder="Select refresh interval"
                  value={field.value ? field.value.toString() : null}
                  searchable
                  onChange={(value) => field.onChange(parseInt(value))}
                />
              );
            }}
          />
        </div>
        <div className="flex items-start gap-x-2">
          <RunQueryButton
            disabled={readOnly || !windowLength || !windowHop}
            featurePreview={featureQueryPreview}
            onRunQuery={handleRunQuery}
          />
        </div>
        <QueryHasChangedCallout previewMutation={featureQueryPreview} />
        <TooFewColumnsCallout
          featureQueryPreview={featureQueryPreview}
          isEditMode={isEditMode}
        />
      </div>
    </Accordion.Item>
  );
};

const QuerySectionSkeleton: React.FC = () => (
  <>
    {/* SQL editor */}
    <div className="h-80 pb-6">
      <Skeleton height="h-full" width="w-full" />
    </div>
    {/* Time window + End date */}
    <div className="flex items-center gap-x-4 pb-4">
      <div className="flex-1">
        <div className="pb-2">
          <Skeleton height="h-5" width="w-24" />
        </div>
        <Skeleton height="h-9" width="w-full" />
      </div>
      <div className="flex-1">
        <div className="pb-2">
          <Skeleton height="h-5" width="w-46" />
        </div>
        <Skeleton height="h-9" width="w-full" />
      </div>
    </div>
    {/* Refresh interval */}
    <div className="pb-6">
      <div className="pb-2">
        <Skeleton height="h-5" width="w-32" />
      </div>
      <Skeleton height="h-9" width="w-full" />
    </div>
    {/* Run Query buttons */}
    <div className="flex items-start gap-x-2">
      <Skeleton height="h-9" rounded="rounded-lg" width="w-52" />
      <Skeleton height="h-9" rounded="rounded-lg" width="w-52" />
    </div>
  </>
);

const QueryHasChangedCallout: React.FC<{
  previewMutation: UseFeatureQueryPreview;
}> = ({ previewMutation }) => {
  const { control, formState } = useFormContext<QueryFormValues>();
  const [queryString, windowLength, windowHop] = useWatch({
    control,
    name: ["query", "window_length", "window_hop"],
  });

  const hasQueryChanged =
    previewMutation.variables?.queryString !== queryString;
  const hasWindowLengthChanged =
    previewMutation.variables?.windowLength !== windowLength;

  const previewSettled =
    previewMutation.isSuccess && !previewMutation.isPending;

  // In edit mode (preview idle, existing query loaded), show callout when
  // query or window_length has been changed from the original values
  const hasQueryFieldsDirtyWhileIdle =
    previewMutation.isIdle &&
    !!formState.defaultValues?.query &&
    !!(formState.dirtyFields.query || formState.dirtyFields.window_length);

  const showCallout =
    (previewSettled && (hasQueryChanged || hasWindowLengthChanged)) ||
    hasQueryFieldsDirtyWhileIdle;

  if (!showCallout) return null;

  const isQueryDirty = previewMutation.isIdle
    ? formState.dirtyFields.query
    : hasQueryChanged;

  const timeWindowChangeMessage = windowHop
    ? "The time window has changed, run the query again"
    : "The time window has changed and the current refresh interval is no longer compatible. Select a new refresh interval and run the query again";

  return (
    <div className="pt-4">
      <Callout variant="warning">
        {isQueryDirty
          ? "The query has changed, run the query again"
          : timeWindowChangeMessage}
      </Callout>
    </div>
  );
};

const TooFewColumnsCallout: React.FC<{
  isEditMode: boolean;
  featureQueryPreview: UseFeatureQueryPreview;
}> = ({ isEditMode, featureQueryPreview }) => {
  const entityName = useWatch({ name: "columns.entities.0.entity_name" });
  const previewColumnCount = getPreviewColumnNames(
    featureQueryPreview.previewData,
  ).length;

  const showCallout =
    isEditMode &&
    !!entityName &&
    featureQueryPreview.isSuccess &&
    !featureQueryPreview.isPending &&
    previewColumnCount < MIN_COLUMNS_FOR_NON_GLOBAL_FEATURES;

  if (!showCallout) return null;

  return (
    <div className="pt-4">
      <Callout variant="error">
        This Feature Query previously has an Entity assigned but now returns
        only one Column. At least two Columns are needed to generate Features
        alongside an Entity.
      </Callout>
    </div>
  );
};
